import React from "react";
import { FormControlLabel, Checkbox, Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../contexts/SnackbarProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ITimer } from "./types";
import TimerTrigger, { TTimerTrigger } from "./TimerTriggerSelect";
import Select from "../../../Components/Select";
import {
  getCommandOptions,
  getCommandParameterOptions,
} from "../../automations/utils";
import useCreateData from "../../../hooks/useCreateData";
import useUpdateData from "../../../hooks/useUpdateData";
import { IControl, IControlCommandBase } from "../types";
import { timerToString } from "./utils";
import { useTranslation } from "react-i18next";
import DialogButtons from "../../../Components/DialogButtons";

interface ITimerPanelProps {
  timer: ITimer;
  control: IControl;
}

const TimerPanel = (props: ITimerPanelProps) => {
  const { timer, control } = props;
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const commandOptions = getCommandOptions(control as IControlCommandBase);
  const initialCommand =
    timer.action.command || (commandOptions[0].value as string);

  const parameterOptions = getCommandParameterOptions(
    control as IControlCommandBase,
    initialCommand as string
  );
  const initialParameter =
    timer.action.parameter || (parameterOptions[0].value as string);

  const navigate = useNavigate();
  const showMessage = useSnackbar();
  const { t } = useTranslation();

  const handleCancel = () => {
    showMessage({
      text:
        timer.id !== -1
          ? t("Editing timer WHAT cancelled", {
              what: timerToString(timer),
            })
          : t("Creating new timer cancelled"),
      severity: "info",
    });
    navigate(`/controls/${control.id}/timers`);
  };

  const createHandler = useCreateData();
  const updateHandler = useUpdateData();
  const formik = useFormik({
    initialValues: {
      is_enabled: timer.is_enabled,
      trigger: timer.trigger,
      action: {
        type: "mqtt",
        control_id: control.id,
        command: initialCommand,
        parameter: initialParameter,
      },
    },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      const allValues = { ...timer, ...values };
      setIsSubmitting(true);
      if (timer.id === -1) {
        createHandler(t("Timer"), `/controls/${control.id}/timers`, allValues);
      } else {
        updateHandler(t("Timer"), `/timers/${timer.id}`, allValues);
      }
      navigate(`/controls/${control.id}/timers`);
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={2} alignItems="flex-start">
        <FormControlLabel
          control={<Checkbox checked={formik.values.is_enabled} />}
          name={"is_enabled"}
          label={t("Enabled")}
          onChange={formik.handleChange}
        />
        <TimerTrigger
          trigger={formik.values.trigger as TTimerTrigger}
          onChange={(trigger: TTimerTrigger) =>
            formik.setFieldValue("trigger", trigger)
          }
        />
        <Box
          gap={1}
          display="flex"
          flexDirection="row"
          style={{ width: "100%" }}
        >
          <Select
            fullWidth
            label={t("Command")}
            value={formik.values.action.command}
            options={commandOptions}
            onChange={(event) => {
              const newCommand = event.target.value;
              const newAction = {
                ...formik.values.action,
                command: newCommand,
              };
              formik.setFieldValue("action", newAction);
            }}
            margin="none"
            style={{ width: "50%" }}
          />
          <Select
            label={t("Parameter")}
            options={parameterOptions}
            value={formik.values.action.parameter}
            onChange={(event) => {
              const newParameter = event.target.value;
              const newAction = {
                ...formik.values.action,
                parameter: newParameter,
              };
              formik.setFieldValue("action", newAction);
            }}
            margin="none"
            style={{ width: "50%" }}
          />
        </Box>
      </Stack>
      <DialogButtons
        isSubmitting={isSubmitting}
        submitButtonLabel="Save"
        resetCallback={handleCancel}
      />
    </form>
  );
};

export default TimerPanel;
